/**
 * The one way an AI-credit balance is drawn.
 *
 * Three surfaces show it (chat header, MMM hero, the AI-report button) and a
 * balance rendered three ways is three things that drift apart — the chat and
 * the hero disagreeing about the same number is exactly the confusion the
 * ledger exists to prevent.
 *
 * Renders nothing when credits are disabled or the balance has not loaded, so
 * a caller can drop it in unconditionally.
 *
 * @module components/CreditChip
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { Coins } from "lucide-react";

export interface CreditChipProps {
  /** null = disabled or not loaded yet — the chip renders nothing. */
  balance: number | null;
  /** 'hero' sits on the yellow band; 'inline' inside white surfaces. */
  variant?: "hero" | "inline";
  /**
   * Credits charged per successful AI call for this workspace. The master can
   * change it per workspace, so the chip states it rather than assuming 1.
   */
  rate?: number | null;
  /** How many upstream calls the neighbouring action spends (a report = 1). */
  costCalls?: number;
  className?: string;
}

/** What the reader needs to plan with, from the workspace's ACTUAL rate. */
/** Module-level, so the translator is passed rather than hooked. */
type TFn = (key: string, options?: Record<string, unknown>) => string;

const rateHint = (rate: number | null | undefined, t: TFn): string =>
  rate === null || rate === undefined
    ? t("credits.rateOne")
    : rate === 0
      ? t("credits.rateFree")
      : t("credits.rateN", { rate: rate.toLocaleString() });

export const CreditChip: React.FC<CreditChipProps> = ({
  balance,
  variant = "inline",
  rate,
  costCalls,
  className,
}) => {
  const { t } = useTranslation();
  if (balance === null) return null;
  const depleted = balance <= 0;

  return (
    <span
      data-credit-chip
      title={rateHint(rate, t)}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full font-semibold tabular-nums whitespace-nowrap",
        variant === "hero" ? "px-3 py-1 text-xs border-2" : "px-2 py-0.5 text-[11px] border",
        depleted
          ? "bg-error-50 border-error-300 text-error-700"
          : variant === "hero"
            ? "bg-white border-ink-950 text-ink-900"
            : "bg-neutral-50 border-neutral-200 text-neutral-700",
        className,
      )}
    >
      <Coins size={variant === "hero" ? 13 : 11} aria-hidden="true" />
      {/* Cost first when this chip sits beside a specific action: the reader is
          deciding whether to spend, not auditing a balance. Computed from the
          workspace's own rate — never a hardcoded 1. */}
      {/* Only when the rate is KNOWN. Falling back to 1 quoted a price the
          workspace may not be on — the master can set any rate, so an assumed
          one is a wrong number wearing a confident label. */}
      {costCalls !== undefined && typeof rate === "number" && (
        <span className="font-normal">{t("credits.spends", { amount: (rate * costCalls).toLocaleString() })}</span>
      )}
      {t("credits.left", { balance: balance.toLocaleString() })}
    </span>
  );
};

export default CreditChip;
